/**
 * Program Builder Types
 * Feature: 028-we-want-to - Program Builder Admin Page
 */

import type { SubscriptionAvailability } from "@/api/bundles/types";

/**
 * Program entity for admin builder
 * Maps to Enhancement Bundle on the backend
 */
export interface Program {
  id: string;
  name: string;
  description: string | null;
  tagline: string | null;
  thumbnailUrl: string | null;
  isPublished: boolean;
  price: number | null;
  subscriptionAvailability: SubscriptionAvailability;
  components: ProgramComponents;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Program content associations
 */
export interface ProgramComponents {
  courses: string[]; // Course IDs
  events: string[]; // Event IDs
  resourceTags: string[]; // Resource tag strings
}

/**
 * Access status for subscription tiers
 */
export type ProgramAccessStatus = "included" | "purchasable" | "not_available";

/**
 * Form values for Stage 1 (create) and Details tab
 */
export interface ProgramDetailsFormValues {
  name: string;
  description: string;
  tagline: string;
  thumbnailUrl: string;
  price: number | null;
  isPublished: boolean;
}

/**
 * Subscription tier configuration for permissions tab
 */
export interface SubscriptionTierConfig {
  tierId: string;
  tierName: string;
  availability: ProgramAccessStatus;
}

/**
 * Available subscription tiers (configured list)
 */
export const SUBSCRIPTION_TIERS: SubscriptionTierConfig[] = [
  { tierId: "basic", tierName: "Basic Access", availability: "not_available" },
  {
    tierId: "virtual_only",
    tierName: "Virtual Only",
    availability: "not_available",
  },
  {
    tierId: "total_access",
    tierName: "Total Access",
    availability: "not_available",
  },
];

/**
 * Get default subscription availability (all tiers set to 'not_available')
 */
export function getDefaultSubscriptionAvailability(): SubscriptionAvailability {
  return SUBSCRIPTION_TIERS.reduce((acc, tier) => {
    acc[tier.tierId] = "not_available";
    return acc;
  }, {} as SubscriptionAvailability);
}

/**
 * Program editor tab types
 */
export type ProgramEditorTab = "details" | "content" | "permissions";

/**
 * Save status for auto-save indicator
 */
export type SaveStatus = "idle" | "saving" | "saved" | "error";
